import { Controller } from "@hotwired/stimulus";
import { makeMatcher } from "../utils/text_match";

// 3-step wizard for editing a Permission's JSONB module flags.
// All checkboxes live in step 2; step 1 is name + optional "clone from"
// preset; step 3 is a recap rendered at runtime from the current checkbox
// state. The form posts the whole permission[...] payload at the end.
export default class extends Controller {
  static targets = [
    "step", "progressStep", "progressDot", "progressLabel",
    "prevBtn", "nextBtn", "submitBtn",
    "nameField", "cloneSelect", "cloneSeeds",
    "domainCount", "domainTotal",
    "search", "moduleRow",
    "recap", "errorBanner"
  ];
  static values = {
    current:    { type: Number, default: 0 },
    totalSteps: { type: Number, default: 3 }
  };

  connect() {
    this._render();
    this._refreshDomainCounts();
  }

  next(event) {
    event.preventDefault();
    if (this.currentValue >= this.totalStepsValue - 1) return;
    if (!this._validateCurrent()) return;
    this.currentValue = this.currentValue + 1;
    if (this.currentValue === this.totalStepsValue - 1) this._renderRecap();
    this._render();
  }

  prev(event) {
    event.preventDefault();
    if (this.currentValue <= 0) return;
    this.currentValue = this.currentValue - 1;
    this._render();
  }

  // Step 2 — toggle every checkbox of a module domain (group).
  toggleDomain(event) {
    const domain = event.currentTarget.dataset.domain;
    const wantChecked = event.currentTarget.dataset.action !== "checked";
    const rows = this.moduleRowTargets.filter((r) => r.dataset.domain === domain);
    rows.forEach((row) => {
      row.querySelectorAll('input[type="checkbox"]').forEach((cb) => { cb.checked = wantChecked; });
    });
    this._refreshDomainCounts();
  }

  // Step 2 — toggle every checkbox on a row (module).
  toggleModule(event) {
    const row = event.currentTarget.closest("[data-module-row]");
    const wantChecked = event.currentTarget.checked;
    row.querySelectorAll('input[data-perm-action]').forEach((cb) => { cb.checked = wantChecked; });
    this._refreshDomainCounts();
  }

  // Step 2 — checkbox change : keep the row toggle in sync.
  cellChanged(event) {
    const row = event.currentTarget.closest("[data-module-row]");
    if (!row) return;
    const cells = row.querySelectorAll('input[data-perm-action]');
    const checked = Array.from(cells).filter((c) => c.checked).length;
    const rowToggle = row.querySelector('[data-row-toggle]');
    if (rowToggle) {
      rowToggle.checked = checked === cells.length;
      rowToggle.indeterminate = checked > 0 && checked < cells.length;
    }
    this._refreshDomainCounts();
  }

  // Step 2 — filter rows by label as the user types.
  filterModules(event) {
    const matcher = makeMatcher(event.currentTarget.value);
    this.moduleRowTargets.forEach((row) => {
      row.toggleAttribute("hidden", !matcher(row.dataset.label || ""));
    });
  }

  // Step 1 — clone the JSONB flags from an existing permission. Source data
  // is rendered as a JSON string in a hidden <script type="application/json">
  // keyed by permission id.
  applyClone(event) {
    const id = event.currentTarget.value;
    if (!id || !this.hasCloneSeedsTarget) return;
    let seeds;
    try {
      seeds = JSON.parse(this.cloneSeedsTarget.textContent || "{}");
    } catch (_e) {
      return;
    }
    const snapshot = seeds[id];
    if (!snapshot) return;

    Object.entries(snapshot).forEach(([moduleKey, actions]) => {
      Object.entries(actions).forEach(([action, value]) => {
        const cb = this.element.querySelector(
          `input[name="permission[${moduleKey}][${action}]"]`
        );
        if (cb) cb.checked = !!value;
      });
    });
    // Sync row toggles + counters to the new state.
    this.moduleRowTargets.forEach((row) => {
      const cells = row.querySelectorAll('input[data-perm-action]');
      const checked = Array.from(cells).filter((c) => c.checked).length;
      const rowToggle = row.querySelector('[data-row-toggle]');
      if (rowToggle) {
        rowToggle.checked = checked === cells.length;
        rowToggle.indeterminate = checked > 0 && checked < cells.length;
      }
    });
    this._refreshDomainCounts();
  }

  _validateCurrent() {
    if (this.currentValue !== 0) return true;
    if (!this.hasNameFieldTarget) return true;
    const ok = this.nameFieldTarget.value.trim().length > 0;
    if (!ok) {
      this.nameFieldTarget.focus();
      this.nameFieldTarget.reportValidity?.();
    }
    return ok;
  }

  _render() {
    this.stepTargets.forEach((s, idx) => {
      const isActive = idx === this.currentValue;
      s.toggleAttribute("hidden", !isActive);
      // The shared `_modal.scss` rule is what actually drives visibility:
      //   .esm_wizard__step           { display: none; }
      //   .esm_wizard__step--active   { display: flex; }
      // Without toggling that class, the step stays display:none even after
      // the [hidden] attribute is removed.
      s.classList.toggle("esm_wizard__step--active", isActive);
      s.classList.toggle("jc-perm-wizard__step--active", isActive);
    });
    this.progressStepTargets.forEach((s, idx) => {
      const isActive = idx === this.currentValue;
      const isDone = idx < this.currentValue;
      s.classList.toggle("esm_wizard__progress-step--active", isActive);
      s.classList.toggle("esm_wizard__progress-step--done", isDone);
    });
    if (this.hasPrevBtnTarget) this.prevBtnTarget.toggleAttribute("hidden", this.currentValue === 0);
    if (this.hasNextBtnTarget) this.nextBtnTarget.toggleAttribute("hidden", this.currentValue === this.totalStepsValue - 1);
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.toggleAttribute("hidden", this.currentValue !== this.totalStepsValue - 1);
  }

  _refreshDomainCounts() {
    this.domainCountTargets.forEach((el) => {
      const domain = el.dataset.domain;
      const cells = this.moduleRowTargets
        .filter((r) => r.dataset.domain === domain)
        .flatMap((r) => Array.from(r.querySelectorAll('input[data-perm-action]')));
      const checked = cells.filter((c) => c.checked).length;
      el.textContent = `${checked}/${cells.length}`;
    });
  }

  _renderRecap() {
    if (!this.hasRecapTarget) return;
    const grouped = {};
    const labels = JSON.parse(this.recapTarget.dataset.labels || "{}");
    this.moduleRowTargets.forEach((row) => {
      const cells = row.querySelectorAll('input[data-perm-action]');
      const active = Array.from(cells).filter((c) => c.checked);
      if (active.length === 0) return;
      const domain = row.dataset.domain;
      const domainLabel = row.dataset.domainLabel;
      const moduleLabel = row.dataset.label;
      grouped[domain] ||= { label: domainLabel, modules: [] };
      const actions = active.map((c) => labels[c.dataset.permAction] || c.dataset.permAction);
      grouped[domain].modules.push({ label: moduleLabel, actions });
    });

    const keys = Object.keys(grouped);
    if (keys.length === 0) {
      this.recapTarget.innerHTML = '<p class="esm_form__hint">Aucune permission cochée — la classe ne donnera accès à rien.</p>';
      return;
    }

    this.recapTarget.innerHTML = keys.map((domain) => {
      const g = grouped[domain];
      const pills = g.modules
        .map((m) => `<span class="jc-perm-recap__pill">${this._escape(m.label)} <span style="opacity:.6;">·</span> ${m.actions.map((a) => this._escape(a)).join(", ")}</span>`)
        .join("");
      return `
        <div class="jc-perm-recap__card">
          <h4 class="jc-perm-recap__title">${this._escape(g.label)}</h4>
          <div class="jc-perm-recap__pills">${pills}</div>
        </div>
      `;
    }).join("");
  }

  _escape(value) {
    const str = String(value ?? "");
    return str.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
  }
}
